import React from 'react';
import AdditionalPayTypesCard from '../../../components/AdditionalPayTypesCard';
import StatHolidayPayCard from '../../../components/StatHolidayPayCard';
import TimeOffCard from '../../../components/TimeOffCard';
import DeductionsCard from '../../../components/DeductionsCard';
import PersonalInfoCard from '../../../components/PersonalInfoCard';
import EmergencyContactsCard from '../../../components/EmergencyContactsCard';
import EmploymentDetailsCard from '../../../components/EmploymentDetailsCard';
import PaymentMethodCard from '../../../components/PaymentMethodCard';

import BasePaySectionCard from './BasePaySectionCard';
import TaxSectionCard from './TaxSectionCard';
import CompensationSectionCard from './CompensationSectionCard';
import FieldEditor from './FieldEditor';

import { FONT } from '../constants';
import { C } from '../styles';
import { sectionStatus, formatViewValue } from '../helpers';
import type { Country, Employee, Section as SectionType, Values, WorkLocation } from '../types';

/* eslint-disable @typescript-eslint/no-explicit-any */
const AdditionalPayTypesCardAny = AdditionalPayTypesCard as any;
const StatHolidayPayCardAny = StatHolidayPayCard as any;
const TimeOffCardAny = TimeOffCard as any;
const DeductionsCardAny = DeductionsCard as any;
const PersonalInfoCardAny = PersonalInfoCard as any;
const EmergencyContactsCardAny = EmergencyContactsCard as any;
const EmploymentDetailsCardAny = EmploymentDetailsCard as any;
const PaymentMethodCardAny = PaymentMethodCard as any;
/* eslint-enable @typescript-eslint/no-explicit-any */

interface Props {
  section: SectionType;
  values: Values;
  draft: Values;
  country: Country;
  isOpen: boolean;
  isEditing: boolean;
  isSaving: boolean;
  disabledByOtherEdit: boolean;
  fieldErrors: Record<string, string | null>;
  onToggleOpen: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onChange: (k: string, val: unknown) => void;
  workLocations: WorkLocation[];
  employeeId: string | number | undefined;
  fullEmployee: Employee | null | undefined;
}

export default function Section({
  section, values, draft, country,
  isOpen, isEditing, isSaving, disabledByOtherEdit,
  fieldErrors, onToggleOpen, onEdit, onCancel, onSave, onChange,
  workLocations, employeeId, fullEmployee,
}: Props) {
  const status = sectionStatus(section, values);
  const pill = status === 'done'
    ? { bg: C.greenSoft, fg: C.green, label: 'Done' }
    : status === 'edit'
      ? { bg: C.tealSoft, fg: C.tealInk, label: 'In progress' }
      : { bg: C.amberSoft, fg: C.amber, label: 'Start' };
  const actLabel = status === 'start' ? 'Start' : 'Edit';
  const v = isEditing ? draft : values;

  // Special-case rendering by section id
  if (section.id === 'basepay') {
    return (
      <BasePaySectionCard
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employee={(fullEmployee || values) as Employee}
        onEditClick={() => window.dispatchEvent(new CustomEvent('novala:openBasePayModal'))}
      />
    );
  }
  if (section.id === 'tax') {
    return (
      <TaxSectionCard
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employee={(fullEmployee || values) as Employee}
        onEditClick={() => window.dispatchEvent(new CustomEvent('novala:openTaxModal'))}
      />
    );
  }
  if (section.id === 'addpay') {
    return (
      <AdditionalPayTypesCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employeeId={employeeId}
      />
    );
  }
  if (section.id === 'statholiday') {
    return (
      <StatHolidayPayCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employeeId={employeeId}
      />
    );
  }
  if (section.id === 'timeoff') {
    return (
      <TimeOffCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employeeId={employeeId}
      />
    );
  }
  if (section.id === 'deductions') {
    return (
      <DeductionsCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employeeId={employeeId}
        employee={values}
      />
    );
  }
  if (section.id === 'personal') {
    return (
      <PersonalInfoCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employee={fullEmployee || values}
      />
    );
  }
  if (section.id === 'emergency') {
    return (
      <EmergencyContactsCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employee={fullEmployee || values}
      />
    );
  }
  if (section.id === 'employment') {
    return (
      <EmploymentDetailsCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employee={fullEmployee || values}
      />
    );
  }
  if (section.id === 'payment') {
    return (
      <PaymentMethodCardAny
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employee={fullEmployee || values}
      />
    );
  }
  if (section.id === 'compensation') {
    return (
      <CompensationSectionCard
        section={section}
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        employeeId={employeeId}
      />
    );
  }

  // Default: field-based rendering
  const visibleFields = section.fields.filter((f) => !f.showIf || f.showIf(v));

  return (
    <div style={{
      background: '#fff',
      border: '1px solid ' + C.line,
      borderRadius: 15,
      boxShadow: '0 1px 2px rgba(16,26,43,0.04)',
      overflow: 'hidden',
    }}>
      <div
        onClick={() => { if (!isEditing && !disabledByOtherEdit) onToggleOpen(); }}
        style={{
          display: 'flex', alignItems: 'center',
          padding: '16px 20px',
          cursor: isEditing || disabledByOtherEdit ? 'default' : 'pointer',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0E1A1A' }}>{section.title}</div>
          {section.intro && (
            <div style={{ fontSize: 12, color: '#12262B', fontWeight: 500, marginTop: 2 }}>{section.intro}</div>
          )}
        </div>
        {status !== 'done' && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 700,
            padding: '3px 9px', borderRadius: 6,
            background: pill.bg, color: pill.fg,
            marginRight: 10, letterSpacing: 0.3, textTransform: 'uppercase',
          }}>
            {pill.label}
          </span>
        )}
        {!isEditing && (
          <a
            onClick={(e) => { e.stopPropagation(); if (!disabledByOtherEdit) onEdit(); }}
            style={{
              fontSize: 13, color: '#0F6E56', fontWeight: 700,
              textDecoration: 'underline',
              cursor: disabledByOtherEdit ? 'not-allowed' : 'pointer',
              marginRight: 12,
              opacity: disabledByOtherEdit ? 0.5 : 1,
            }}
          >
            {actLabel}
          </a>
        )}
        <span style={{ color: '#12262B', fontSize: 14 }}>{isOpen ? '▾' : '▸'}</span>
      </div>
      {isOpen && (
        <div style={{ padding: '4px 22px 22px', borderTop: '1px solid ' + C.lineSoft }}>
          {isEditing ? (
            <div>
              {section.intro && (
                <div style={{
                  marginTop: 10, padding: '12px 14px',
                  background: C.tealSoft, borderRadius: 10,
                  fontSize: 13, color: C.tealInk, lineHeight: 1.5,
                }}>
                  {section.intro}
                </div>
              )}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '14px 18px', padding: '18px 0 4px',
              }}>
                {visibleFields.map((f) => (
                  <FieldEditor
                    key={f.k}
                    field={f.t === 'wloc-select' ? { ...f, workLocations } : f}
                    value={draft[f.k]}
                    error={fieldErrors[f.k] || undefined}
                    onChange={(val) => onChange(f.k, val)}
                  />
                ))}
              </div>
              {section.id === 'tax' && country && country.calculationSource && (
                <div style={{
                  marginTop: 18, padding: '10px 14px',
                  background: C.lineSoft, borderRadius: 8,
                  fontSize: 12, color: C.muted, lineHeight: 1.45,
                }}>
                  {country.calculationSource}
                </div>
              )}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                gap: 10, padding: '16px 0 2px', marginTop: 6,
                borderTop: '1px solid ' + C.lineSoft,
              }}>
                <button
                  onClick={onCancel}
                  disabled={isSaving}
                  style={{
                    fontFamily: FONT, fontWeight: 600, fontSize: 14,
                    background: '#fff', border: '1px solid ' + C.line,
                    color: C.ink, borderRadius: 10, padding: '9px 18px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  disabled={isSaving}
                  style={{
                    fontFamily: FONT, fontWeight: 600, fontSize: 14,
                    background: isSaving ? '#C3CBD6' : C.teal,
                    color: '#fff', border: '1px solid transparent',
                    borderRadius: 10, padding: '9px 18px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    boxShadow: isSaving ? 'none' : '0 1px 2px rgba(21,160,140,0.3)',
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save ' + (section.shortTitle || section.title.split(' (')[0])}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {visibleFields.map((f) => {
                const display = formatViewValue(f.t === 'wloc-select' ? { ...f, workLocations } : f, v[f.k]);
                return (
                  <div
                    key={f.k}
                    style={{
                      display: 'grid', gridTemplateColumns: '220px 1fr',
                      gap: '10px 24px', alignItems: 'start',
                      padding: '14px 0',
                      borderBottom: '1px solid ' + C.lineSoft,
                    }}
                  >
                    <span style={{ fontSize: 13.5, color: '#12262B', fontWeight: 700 }}>{f.l}</span>
                    <span style={{
                      fontSize: 13.5,
                      color: display ? '#0E1A1A' : '#94A0B2',
                      fontWeight: 500,
                    }}>
                      {display || 'Not set'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}